<roots-page-header [toolTip]="toolTip" [title]="title" [loading]="loading" (refresh)="getAssetData(assetId)">
</roots-page-header>

@if (asset) {
  <div class="asset-details-layout">
    <div class="asset-editor">
      <section class="details-section">
        <div class="section-heading">
          <span class="section-icon"><mat-icon>devices</mat-icon></span>
          <div>
            <h2>Asset profile</h2>
            <p>Device identity and customer-facing details.</p>
          </div>
        </div>
        <dl class="asset-identity">
          <div><dt>Asset name</dt><dd>{{ asset.basicName || '—' }}</dd></div>
          <div><dt>Asset</dt><dd>{{ model?.basicName || '—' }}</dd></div>
          <div class="identity-wide"><dt>Code</dt><dd class="asset-code">{{ asset.code || '—' }}</dd></div>
          @if (activeScout) {
            <div><dt>Firmware version</dt><dd>{{ asset.firmwareVersion || '—' }}</dd></div>
          }
          <div><dt>Created</dt><dd>{{ (asset.createdAt | date: 'medium') || '—' }}</dd></div>
          <div><dt>Last updated</dt><dd>{{ (asset.updatedAt | date: 'medium') || '—' }}</dd></div>
        </dl>
        <div class="details-fields">
          @if (activeScout) {
            <mat-form-field appearance="outline">
              <mat-label>Scout name</mat-label>
              <input matInput [(ngModel)]="activeScout.name" required />
            </mat-form-field>
          }
          @if (activeSensorName) {
            <mat-form-field appearance="outline">
              <mat-label>Sensor name</mat-label>
              <input matInput [(ngModel)]="activeSensorName" required />
            </mat-form-field>
          }
          <mat-form-field appearance="outline" class="field-wide">
            <mat-label>Description</mat-label>
            <textarea matInput rows="2" [(ngModel)]="asset.basicDescription" required></textarea>
          </mat-form-field>
        </div>
      </section>

      <section class="details-section">
        <div class="section-heading">
          <span class="section-icon"><mat-icon>tune</mat-icon></span>
          <div>
            <h2>Model & status</h2>
            <p>Manage the asset model and current device status.</p>
          </div>
        </div>
        <div class="model-edit-notice" [class.model-unlocked]="MODEL_CHANGE">
          <mat-icon>{{ MODEL_CHANGE ? 'lock_open' : 'lock_outline' }}</mat-icon>
          <div>
            <strong>{{ MODEL_CHANGE ? 'Model editing enabled' : 'Asset model is locked' }}</strong>
            <p>Only change the model if you know which model this device requires.</p>
          </div>
          @if (!MODEL_CHANGE) {
            <button mat-stroked-button type="button" (click)="enableModelChange()">Unlock model</button>
          } @else {
            <button mat-stroked-button type="button" (click)="disableModelChange()">Lock model</button>
          }
        </div>
        <div class="details-fields">
          <mat-form-field appearance="outline">
            <mat-label>Asset model</mat-label>
            <mat-select [(ngModel)]="asset.assetModelId" name="model" [disabled]="MODEL_CHANGE == false">
              @for (model of assetModel | sort: 'code'; track model) {
                <mat-option [value]="model.id" (click)="modelChanged(model)">{{ model.basicName }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Asset status</mat-label>
            <mat-select [(ngModel)]="asset.assetStatuslId" name="status">
              @for (status of assetStatus | sort: 'code'; track status) {
                <mat-option [value]="status.id">{{ status.basicName }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          @if (asset.connectivityBaseScoutConnectivityCode && asset.connectivityBaseScoutConnectivityCode !== null) {
            <mat-form-field appearance="outline" class="field-wide">
              <mat-label>Base connectivity code</mat-label>
              <input matInput [(ngModel)]="asset.connectivityBaseScoutConnectivityCode" />
            </mat-form-field>
          }
        </div>
      </section>
      <div class="asset-save-bar">
        <span>Review asset details before saving.</span>
        <button mat-raised-button type="button" color="primary" [disabled]="loading" (click)="onUpdate(asset)">
          <mat-icon>save</mat-icon>Save changes
        </button>
      </div>

      @if (scoutId && ownershipHistory) {
        <section class="details-section scout-settings">
          <div class="section-heading">
            <span class="section-icon"><mat-icon>schedule</mat-icon></span>
            <div>
              <h2>Scout communication</h2>
              <p>Configure polling, sending intervals, and device commands.</p>
            </div>
          </div>
          @if (scout) {
            <app-scout-interval [scoutId]="scoutId" [sendCounterValue]="sendInterval"
              [pollCounterValue]="pollInterval" [scout]="scout" [firmwareVersion]="firmwareVersion">
            </app-scout-interval>
          } @else {
            <p class="settings-unavailable">Scout communication settings have not loaded. Refresh to try again.</p>
          }
        </section>
      }
      @if (installs.length) {
        <section class="asset-location" aria-label="Asset location">
          <app-asset-map [installs]="installs" [coordinates]="coordinates"></app-asset-map>
        </section>
      }
    </div>
    <aside class="asset-related" aria-label="Related asset information">
      <div class="related-heading">
        <h2>Asset activity</h2>
        <p>Notes, configurations, and device history.</p>
      </div>
      <app-notes [compact]="true" [entityId]="asset.id" [entityCode]="asset.code" [entityType]="'Asset'" [notes]="notes"
        [isLong]="true" (noteAdded)="noteAdded($event)">
      </app-notes>
      <app-roots-details-data-list [compact]="true" [data]="scouts" [prop_1]="'assetName'" [prop_2]="'content'" [type]="1"
        [icon]="'settings_input_antenna'" [title]="'Scouts'" [isLong]="true">
      </app-roots-details-data-list>
      <app-roots-details-data-list [compact]="true" [data]="ownershipHistory" [prop_1]="'customerName'" [prop_2]="'assetName'"
        [prop_3]="'whenBegin'" [prop_4]="'whenEnd'" [type]="3" [icon]="'history'" [title]="'Ownership History'" [isLong]="true">
      </app-roots-details-data-list>
      <app-roots-details-data-list [compact]="true" [data]="formattedInstalls" [prop_1]="'whenBegin'" [prop_2]="'whenEnd'"
        [prop_3]="'coordinateLatitude'" [prop_4]="'coordinateLongitude'" [prop_5]="'coordinateAltitude'" [type]="4"
        [icon]="'place'" [title]="'Installs'" [isLong]="true">
      </app-roots-details-data-list>
    </aside>
  </div>
}
